import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form } from "@ant-design/compatible";
import { Button, Input, Drawer, Divider, Checkbox, DatePicker, InputNumber, Row, Col, Spin } from "antd";
import { _newLine, isValidDateForm, laboCaseStatuses } from '@/utils/helpers';
import BaseRadioList from '@/components/Elements/BaseRadios';
import { createLaboCase, updateLaboCase } from "@/redux/actions/laboCaseActions";
import { fetchActiveLabos } from "@/redux/actions/laboActions";
import { isMobile } from "react-device-detect";
import BaseSelect from "@/components/Elements/BaseSelect";
import CustomerSelect from "@/components/customers/CustomerSelect";
import { fetchCustomerById } from "@/redux/actions/customerActions";
import ImageSelector from "@/components/ImageSelector";

function LaboCaseForm({
    form,
    visible,
    currentItem,
    onCancel,
    loadData
}) {
    const { getFieldDecorator, resetFields, validateFields } = form;
    const [isLoading, setIsLoading] = useState(false);
    const [customer, setCustomer] = useState(null);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [serviceLoading, setServiceLoading] = useState(false);

    const dispatch = useDispatch();
    const laboList = useSelector((state) => state.laboReducer.activeList.items);

    useEffect(() => {
        dispatch(fetchActiveLabos());
    }, [dispatch]);

    useEffect(() => {
        if (
            visible &&
            currentItem &&
            (!form.prevVisible || currentItem.id !== form.prevItemId)
        ) {
            setSelectedCustomer(currentItem.customer.id);
        }
        form.prevVisible = visible;
        form.prevItemId = currentItem?.id;
    }, [visible, currentItem]);

    useEffect(() => {
        if (visible && currentItem?.images?.length) {
            const formattedImages = currentItem.images.map((img) => ({
                manual_id: img.manual_id,
                src: img.url || img.src || img, // tuỳ thuộc dữ liệu backend
            }));
            setUploadedImages(formattedImages);
        } else if (!visible) {
            // Reset images khi đóng form
            setUploadedImages([]);
        }
    }, [visible, currentItem]);

    const setSelectedCustomer = async (id) => {
        setServiceLoading(true);
        try {
            const res = await dispatch(fetchCustomerById(id));
            setCustomer(res);
        } catch (err) { } finally {
            setServiceLoading(false);
        }
    };

    const handleImageChange = (fileList, changeDetails) => {
        const images = fileList.map((file) => ({
            manual_id: file.manual_id,
            src: file.originalImage,
        }));
        setUploadedImages(images);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        validateFields(async (err, values) => {
            if (!err) {
                setIsLoading(true);
                values.images = uploadedImages;
                try {
                    if (currentItem) {
                        await dispatch(updateLaboCase(currentItem.id, values));
                    } else {
                        await dispatch(createLaboCase(values));
                    }
                    loadData();
                    handleClose();
                } catch (error) {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleClose = () => {
        resetFields();
        setIsLoading(false);
        onCancel();
    };

    const customerInitialValue = currentItem?.customer
        ? {
            value: currentItem.customer.id,
            label: `${currentItem.customer.full_name} (${currentItem.customer.code})`,
        }
        : null;

    return (
        <Drawer
            visible={visible}
            title={currentItem ? "Sửa bản ghi" : "Thêm mới bản ghi"}
            width={isMobile ? "100%" : "50%"}
            onClose={handleClose}
            maskClosable={false}
            destroyOnClose
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button onClick={handleClose}>Huỷ</Button>
                    <Divider type="vertical" />
                    <Button loading={isLoading} type="primary" onClick={handleSubmit}>
                        {currentItem ? "Cập nhật" : "Thêm mới"}
                    </Button>
                </div>
            }
        >
            {visible && (
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Row gutter={16}>
                            <Col xs={24} md={24}>
                                <Form.Item label="Labo">
                                    {getFieldDecorator("labo_id", {
                                        rules: [
                                            {
                                                required: true,
                                                message: "Trường bắt buộc!",
                                            },
                                        ],
                                        initialValue: currentItem ? currentItem.labo_id : null,
                                    })(
                                        <BaseSelect
                                            options={laboList || []}
                                            optionValue="id"
                                            optionLabel="name"
                                            showSearch
                                            placeholder="Chọn labo"
                                            style={{ width: '100%' }}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Khách hàng">
                                    {getFieldDecorator("customer_id", {
                                        rules: [
                                            {
                                                required: true,
                                                message: "Trường bắt buộc!",
                                            },
                                        ],
                                        initialValue: customerInitialValue
                                    })(
                                        <CustomerSelect
                                            onChange={(id, label) => setSelectedCustomer(id, label)}
                                        />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Dịch vụ">
                                    <Spin spinning={serviceLoading}>
                                        {customer?.used_services?.length > 0 ? (
                                            getFieldDecorator("customer_service_ids", {
                                                rules: [
                                                    {
                                                        required: true,
                                                        message: "Trường bắt buộc!",
                                                    },
                                                ],
                                                initialValue: currentItem?.customer_service_ids || [],
                                            })(
                                                <Checkbox.Group style={{ width: '100%' }}>
                                                    {customer.used_services.map((service) => (
                                                        <Checkbox
                                                            key={service.id}
                                                            value={service.id}
                                                        >
                                                            {service.service?.name}
                                                        </Checkbox>
                                                    ))}
                                                </Checkbox.Group>
                                            )
                                        ) : (
                                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                                                Không có dịch vụ nào.
                                            </div>
                                        )}
                                    </Spin>
                                </Form.Item>

                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày gửi">
                                    {getFieldDecorator('send_date', {
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Trường bắt buộc!',
                                            },
                                        ],
                                        initialValue: isValidDateForm(currentItem?.send_date, true)
                                    })(
                                        <DatePicker
                                            className="w-100"
                                            format="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày nhận">
                                    {getFieldDecorator('receive_date', {
                                        rules: [
                                            {
                                                required: false,
                                                message: 'Trường bắt buộc!',
                                            },
                                        ],
                                        initialValue: isValidDateForm(currentItem?.receive_date, false)
                                    })(
                                        <DatePicker
                                            className="w-100"
                                            format="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Thời gian bảo hành (tháng)">
                                    {getFieldDecorator('warranty_period', {
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Trường bắt buộc!',
                                            },
                                        ],
                                        initialValue: currentItem?.warranty_period || 0
                                    })(<InputNumber className="w-100" />)}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label="Ngày trả">
                                    {getFieldDecorator('return_date', {
                                        rules: [
                                            {
                                                required: false,
                                                message: 'Trường bắt buộc!',
                                            },
                                        ],
                                        initialValue: isValidDateForm(currentItem?.return_date, false)
                                    })(
                                        <DatePicker
                                            className="w-100"
                                            format="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Trạng thái">
                                    {getFieldDecorator('status', {
                                        rules: [
                                            {
                                                required: true,
                                                message: "Vui lòng chọn!",
                                            },
                                        ],
                                        initialValue: currentItem ? currentItem.status : 1
                                    })(
                                        <BaseRadioList
                                            options={laboCaseStatuses}
                                        />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Ảnh giao nhận">
                                    <ImageSelector
                                        onChangeData={(fileList, changeDetails) => handleImageChange(fileList, changeDetails)} // Gắn hàm xử lý thay đổi dữ liệu
                                        multiple={true} // Cho phép chọn nhiều ảnh
                                        maxCount={50} // Giới hạn tối đa 50 ảnh
                                        uploadText="Chọn ảnh"
                                        defaultFileList={currentItem ? currentItem.images : []}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                                <Form.Item label="Ghi chú">
                                    {getFieldDecorator('note', {
                                        rules: [
                                            {
                                                required: false,
                                                message: "Vui lòng điền!",
                                            },
                                        ],
                                        initialValue: currentItem ? _newLine(currentItem.note) : ""
                                    })(
                                        <Input.TextArea rows={4} />
                                    )}
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={24}>
                            </Col>
                        </Row>
                    </Row>
                </Form>
            )}
        </Drawer>
    );
}

export default Form.create({ name: "LaboCaseForm" })(LaboCaseForm);
